/**
 * KebLayer Module
 *
 * Renders Knowledge Extension Button (KEB) markers on the map.
 * KEBs provide educational overlays about cellular processes, facts, diseases, and medications.
 */

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Style, Icon, Fill, Stroke, Circle as CircleStyle } from 'ol/style';

/**
 * Icon paths for KEB types (SVG icons)
 */
const KEB_ICON_PATHS = {
  wiki: '/icons/kebs/wiki.svg',
  'fun-fact': '/icons/kebs/fun-fact.svg',
  disease: '/icons/kebs/disease.svg',
  medication: '/icons/kebs/medication.svg',
  default: '/icons/kebs/default.svg'
};

/**
 * Color mapping for KEB types
 */
const KEB_COLORS = {
  wiki: '#3b82f6',
  'fun-fact': '#f59e0b',
  disease: '#ef4444',
  medication: '#10b981',
  default: '#6b7280'
};

/**
 * KebLayer manages the display of KEB markers on the map.
 */
export class KebLayer {
  /**
   * Create KEB layer
   * @param {Object} cellMap - CellMap instance
   */
  constructor(cellMap) {
    this.cellMap = cellMap;
    this.map = cellMap.getMap();
    this.source = new VectorSource();
    this.kebs = new Map(); // id -> keb data
    this.hoveredFeature = null;

    this.layer = new VectorLayer({
      source: this.source,
      style: (feature) => this.getStyle(feature),
      // Only show KEBs when zoomed in (resolution <= 8 = zoom level 4+)
      maxResolution: 8,
      zIndex: 10 // Above structure layer
    });

    this.map.addLayer(this.layer);
    this.setupHover();
  }

  /**
   * Load KEBs from JSON file
   * @returns {Promise<void>}
   */
  async loadKebs() {
    try {
      const response = await fetch('/data/kebs.json');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      this.createFeatures(data.kebs);

      console.info(`KEBs loaded: ${data.kebs.length} markers`);
    } catch (error) {
      console.error('Failed to load KEBs:', error);
      throw error;
    }
  }

  /**
   * Create map features from KEB data
   * @param {Array} kebs - Array of KEB objects
   */
  createFeatures(kebs) {
    const features = kebs.map((keb) => {
      const point = new Point([keb.position.x, keb.position.y]);

      const feature = new Feature({
        geometry: point
      });

      feature.setId(`keb-${keb.id}`);
      feature.set('kebId', keb.id);
      feature.set('featureType', 'keb');
      feature.set('kebType', keb.type);
      feature.set('name', keb.name);
      feature.set('description', keb.description);
      feature.set('resolution', keb.resolution);
      feature.set('linkToMore', keb.linkToMore);
      feature.set('parentId', keb.parentId);

      // Store full KEB data
      this.kebs.set(keb.id, keb);

      return feature;
    });

    this.source.addFeatures(features);
  }

  /**
   * Get style for a KEB feature
   * Uses SVG icon with white background circle for visibility
   * @param {Feature} feature - OpenLayers feature
   * @returns {Style[]}
   */
  getStyle(feature) {
    const kebType = feature.get('kebType') || 'default';
    const isHovered = feature.get('hovered');

    const color = KEB_COLORS[kebType] || KEB_COLORS.default;
    const radius = isHovered ? 16 : 12;
    const iconScale = isHovered ? 0.85 : 0.65;
    const iconPath = KEB_ICON_PATHS[kebType] || KEB_ICON_PATHS.default;

    return [
      // Background circle (white with colored border)
      new Style({
        image: new CircleStyle({
          radius: radius,
          fill: new Fill({ color: 'white' }),
          stroke: new Stroke({
            color: color,
            width: 2.5
          })
        })
      }),
      // SVG icon overlay
      new Style({
        image: new Icon({
          src: iconPath,
          scale: iconScale,
          color: color,
          anchor: [0.5, 0.5],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction'
        })
      })
    ];
  }

  /**
   * Set up hover effects for KEB markers
   */
  setupHover() {
    this.map.on('pointermove', (event) => {
      if (event.dragging) {
        return;
      }

      const feature = this.map.forEachFeatureAtPixel(
        event.pixel,
        (f) => f,
        { layerFilter: (l) => l === this.layer }
      );

      // Unhover previous feature
      if (this.hoveredFeature && this.hoveredFeature !== feature) {
        this.hoveredFeature.set('hovered', false);
        this.hoveredFeature.changed();
      }

      // Hover new feature
      if (feature && feature.get('featureType') === 'keb') {
        feature.set('hovered', true);
        feature.changed();
        this.hoveredFeature = feature;
        this.map.getTargetElement().style.cursor = 'pointer';
      } else if (this.hoveredFeature) {
        this.hoveredFeature = null;
        // Don't reset cursor - let structure layer handle it
      }
    });
  }

  /**
   * Get KEB data by ID
   * @param {number} id - KEB ID
   * @returns {Object|undefined}
   */
  getKebById(id) {
    return this.kebs.get(id);
  }

  /**
   * Get all KEBs
   * @returns {Array}
   */
  getAllKebs() {
    return Array.from(this.kebs.values());
  }

  /**
   * Get KEB count
   * @returns {number}
   */
  getKebCount() {
    return this.kebs.size;
  }

  /**
   * Get KEBs by type
   * @param {string} type - KEB type (wiki, fun-fact, disease, medication)
   * @returns {Array}
   */
  getKebsByType(type) {
    return this.getAllKebs().filter((k) => k.type === type);
  }

  /**
   * Get feature by KEB ID
   * @param {number} id - KEB ID
   * @returns {Feature|null}
   */
  getFeatureById(id) {
    return this.source.getFeatureById(`keb-${id}`) || null;
  }

  /**
   * Get the OpenLayers layer
   * @returns {VectorLayer}
   */
  getLayer() {
    return this.layer;
  }

  /**
   * Get the source
   * @returns {VectorSource}
   */
  getSource() {
    return this.source;
  }
}
